<template>
  <main class="model-page">
    <div class="model-nav">
      <el-button-group>
        <el-button
          :type="currentStep === 'review' ? 'primary' : 'default'"
          @click="showReview"
        >
          {{ isEn ? 'Train Model' : '训练模型' }}
        </el-button>
        <el-button
          :type="currentStep === 'train' ? 'primary' : 'default'"
          @click="showTrain"
        >
          {{ isEn ? 'Train Updated Model' : '训练更新模型' }}
        </el-button>
        <el-button
          :type="currentStep === 'history' ? 'primary' : 'default'"
          @click="showHistory"
        >
          {{ isEn ? 'History Results' : '历史结果页面' }}
        </el-button>
      </el-button-group>
    </div>

    <section v-if="currentStep === 'review'" class="review-section">
      <div class="section-head">
        <div>
          <h2>{{ isEn ? 'Review New Data' : '审查新数据' }}</h2>
          <p>{{ isEn ? 'Review manually annotated total-muscle overlay images before adding them.' : '显示全肌肉手动标注归档中的 overlay 图片，可选择或取消选择。' }}</p>
        </div>
        <div class="review-actions">
          <el-switch
            v-model="showHiddenOverlays"
            :active-text="isEn ? 'Show removed' : '显示已移除数据'"
            @change="loadOverlays"
          />
          <el-button icon="el-icon-refresh" :loading="loading" @click="loadOverlays">
            {{ isEn ? 'Refresh' : '刷新' }}
          </el-button>
        </div>
      </div>

      <el-empty
        v-if="!loading && !overlays.length"
        :description="emptyOverlayText"
      />

      <div v-else v-loading="loading" class="overlay-grid">
        <div
          v-for="item in overlays"
          :key="item.filename"
          class="overlay-item"
          :class="{ selected: isSelected(item.filename) }"
        >
          <div class="item-top">
            <el-checkbox
              :value="isSelected(item.filename)"
              @change="toggleSelection(item.filename)"
            />
            <span class="filename" :title="item.filename">{{ item.filename }}</span>
            <el-tag v-if="item.hidden" size="mini" type="info">
              {{ isEn ? 'Removed' : '已移除' }}
            </el-tag>
          </div>
          <el-image
            class="overlay-image"
            :src="imageUrl(item.filename)"
            fit="contain"
            :preview-src-list="previewList"
          />
          <div class="meta-row">
            <span>{{ formatSize(item.size) }}</span>
            <span>{{ item.mtime || '-' }}</span>
          </div>
        </div>
      </div>

      <div class="confirm-bar">
        <span>{{ isEn ? 'Selected' : '已选择' }}: {{ selectedFiles.length }}</span>
        <el-button
          type="danger"
          plain
          :loading="deleting"
          :disabled="!selectedFiles.length || confirming"
          @click="deleteSelected"
        >
          {{ isEn ? 'Delete' : '删除' }}
        </el-button>
        <el-button
          type="primary"
          :loading="confirming"
          :disabled="!selectedFiles.length || deleting"
          @click="confirmSelected"
        >
          {{ isEn ? 'Confirm New Data' : '确认新增数据' }}
        </el-button>
      </div>
    </section>

    <section v-else-if="currentStep === 'train'" ref="trainSection" class="train-section">
      <h2>{{ isEn ? 'Train Updated Model' : '训练更新模型' }}</h2>
      <p v-if="preparedDataset" class="dataset-summary">
        {{ datasetSummaryText }}
      </p>
      <el-empty
        v-else
        :description="isEn ? 'Please confirm new data before starting training.' : '请先在“训练模型”页面确认新增数据。'"
      />
      <div v-if="preparedDataset" class="training-actions">
        <el-button
          type="primary"
          class="train-button"
          :loading="trainingInProgress"
          :disabled="trainingInProgress"
          @click="startTraining"
        >
          {{ isEn ? 'Start Training' : '开始训练' }}
        </el-button>
        <el-button
          v-if="trainingCanStop"
          type="danger"
          plain
          class="train-button"
          :loading="stoppingTraining"
          :disabled="stoppingTraining"
          @click="stopTraining"
        >
          {{ isEn ? 'Stop Training' : '停止训练' }}
        </el-button>
      </div>
      <p v-if="trainingProgressText" class="training-progress">
        {{ trainingProgressText }}
      </p>
      <p v-if="trainingStatusText" class="training-status">
        {{ trainingStatusText }}
      </p>
      <p v-if="trainingTaskId" class="training-task">
        {{ isEn ? 'Training task' : '训练任务' }}: {{ trainingTaskId }}
      </p>
    </section>

    <section v-else-if="currentStep === 'history'" class="history-section">
      <h2>{{ isEn ? 'History Results' : '历史结果页面' }}</h2>
      <el-table
        v-loading="historyLoading"
        class="history-table"
        :data="modelResults"
        border
        empty-text="暂无历史结果"
      >
        <el-table-column
          prop="name"
          :label="isEn ? 'Model ID' : '模型编号'"
          min-width="260"
        />
        <el-table-column
          prop="mtime"
          :label="isEn ? 'Training Date' : '训练日期'"
          width="180"
        />
        <el-table-column
          :label="isEn ? 'Actions' : '操作'"
          width="180"
          align="center"
        >
          <template slot-scope="scope">
            <el-button size="mini" type="danger" plain @click="placeholderAction('delete', scope.row)">
              {{ isEn ? 'Delete' : '删除' }}
            </el-button>
            <el-button size="mini" type="primary" plain @click="placeholderAction('use', scope.row)">
              {{ isEn ? 'Use' : '使用' }}
            </el-button>
          </template>
        </el-table-column>
      </el-table>
    </section>
  </main>
</template>

<script>
import { BASE_URL } from '@/api'

const MODEL_UPDATE_STATE_KEY = 'idoctor_admin_model_update_state'

export default {
  name: 'AdminModels',
  data() {
    return {
      loading: false,
      confirming: false,
      deleting: false,
      training: false,
      stoppingTraining: false,
      showHiddenOverlays: false,
      overlays: [],
      overlayArchiveSummary: {
        totalCount: 0,
        hiddenCount: 0
      },
      selectedFiles: [],
      preparedDataset: null,
      trainingTaskId: '',
      trainingTaskStatus: '',
      trainingEpoch: null,
      trainingMessage: '',
      trainingPollTimer: null,
      currentStep: 'review',
      historyLoading: false,
      modelResults: []
    }
  },
  computed: {
    isEn() {
      return this.$i18n.locale === 'en'
    },
    previewList() {
      return this.overlays.map((item) => this.imageUrl(item.filename))
    },
    emptyOverlayText() {
      const hiddenCount = this.overlayArchiveSummary.hiddenCount || 0
      const totalCount = this.overlayArchiveSummary.totalCount || 0
      if (!this.showHiddenOverlays && hiddenCount > 0) {
        return this.isEn
          ? `No visible overlay images. ${hiddenCount} removed image group(s) can be shown.`
          : `暂无可审查的 overlay 图片，已有 ${hiddenCount} 组图片被移除，可打开“显示已移除数据”查看。`
      }
      if (totalCount <= 0) {
        return this.isEn
          ? 'No archived overlay images. Please manually annotate both psoas and total muscle first.'
          : '暂无归档 overlay 图片，请先在结果详情页同时手动标注腰大肌和全肌肉。'
      }
      return this.isEn ? 'No overlay images found' : '暂无 overlay 图片'
    },
    datasetSummaryText() {
      if (!this.preparedDataset) return ''
      if (this.isEn) {
        return `Currently selected ${this.preparedDataset.selectedCount} data item(s), generated ${this.preparedDataset.sampleCount} training sample(s), training data ID is ${this.preparedDataset.datasetName}.`
      }
      return `当前选择了${this.preparedDataset.selectedCount}份数据，共生成${this.preparedDataset.sampleCount}份训练样本，训练数据编号是${this.preparedDataset.datasetName}`
    },
    trainingInProgress() {
      return this.training || this.trainingTaskStatus === 'processing' || this.trainingTaskStatus === 'stopping'
    },
    trainingCanStop() {
      return !!this.trainingTaskId && (this.trainingTaskStatus === 'processing' || this.trainingTaskStatus === 'stopping')
    },
    trainingProgressText() {
      if (this.trainingEpoch) {
        return `epoch ${this.trainingEpoch.current}/${this.trainingEpoch.total}`
      }
      if (this.trainingTaskStatus === 'processing') {
        return 'epoch 0/500'
      }
      return ''
    },
    trainingStatusText() {
      if (this.trainingTaskStatus === 'stopping') {
        return this.isEn ? 'Stopping training...' : '正在停止训练...'
      }
      if (this.trainingTaskStatus === 'stopped') {
        return this.isEn ? 'Training stopped.' : '训练已停止。'
      }
      if (this.trainingTaskStatus === 'completed') {
        return this.isEn ? 'Training completed.' : '训练完成。'
      }
      if (this.trainingTaskStatus === 'failed') {
        return this.trainingMessage || (this.isEn ? 'Training failed.' : '训练失败。')
      }
      return ''
    }
  },
  created() {
    this.restoreModelUpdateState()
    this.loadOverlays()
    if (this.currentStep === 'history') {
      this.loadModelResults()
    }
  },
  beforeDestroy() {
    this.saveModelUpdateState()
    this.stopTrainingPolling()
  },
  methods: {
    showReview() {
      this.currentStep = 'review'
      this.saveModelUpdateState()
    },
    showTrain() {
      this.currentStep = 'train'
      this.saveModelUpdateState()
    },
    showHistory() {
      this.currentStep = 'history'
      this.saveModelUpdateState()
      this.loadModelResults()
    },
    normalizeModelUpdateStep(step) {
      return ['review', 'train', 'history'].includes(step) ? step : 'review'
    },
    saveModelUpdateState() {
      const state = {
        currentStep: this.normalizeModelUpdateStep(this.currentStep),
        preparedDataset: this.preparedDataset,
        trainingTaskId: this.trainingTaskId,
        trainingTaskStatus: this.trainingTaskStatus,
        trainingEpoch: this.trainingEpoch,
        trainingMessage: this.trainingMessage,
        savedAt: Date.now()
      }
      localStorage.setItem(MODEL_UPDATE_STATE_KEY, JSON.stringify(state))
    },
    restoreModelUpdateState() {
      const raw = localStorage.getItem(MODEL_UPDATE_STATE_KEY)
      if (!raw) return
      try {
        const state = JSON.parse(raw)
        this.currentStep = this.normalizeModelUpdateStep(state.currentStep)
        if (state.preparedDataset && state.preparedDataset.datasetName) {
          this.preparedDataset = state.preparedDataset
        }
        this.trainingTaskId = state.trainingTaskId || ''
        this.trainingTaskStatus = state.trainingTaskStatus || ''
        this.trainingEpoch = state.trainingEpoch || null
        this.trainingMessage = state.trainingMessage || ''
        this.training = false
        if (this.trainingTaskId || this.preparedDataset) {
          this.currentStep = ['completed', 'failed', 'stopped', 'not_found'].includes(this.trainingTaskStatus)
            ? this.currentStep
            : 'train'
        }
        if (this.trainingTaskId && (this.trainingTaskStatus === 'processing' || this.trainingTaskStatus === 'stopping')) {
          this.trainingTaskStatus = this.trainingTaskStatus || 'processing'
          this.startTrainingPolling()
        }
      } catch (error) {
        localStorage.removeItem(MODEL_UPDATE_STATE_KEY)
      }
    },
    async loadModelResults() {
      this.historyLoading = true
      try {
        const res = await this.$http.get(`${BASE_URL}/admin/manual-archive/model-results`)
        this.modelResults = (res.data && res.data.results) || []
      } catch (error) {
        this.$message.error(error.response?.data?.detail || (this.isEn ? 'Failed to load history results' : '加载历史结果失败'))
      } finally {
        this.historyLoading = false
      }
    },
    placeholderAction(action, row) {
      const actionText = action === 'delete'
        ? (this.isEn ? 'Delete' : '删除')
        : (this.isEn ? 'Use' : '使用')
      this.$message.info(`${actionText}: ${row.name}`)
    },
    async loadOverlays() {
      this.loading = true
      try {
        const res = await this.$http.get(`${BASE_URL}/admin/manual-archive/combo-overlays`, {
          params: {
            include_hidden: this.showHiddenOverlays
          }
        })
        this.overlays = (res.data && res.data.images) || []
        this.overlayArchiveSummary = {
          totalCount: (res.data && res.data.total_count) || 0,
          hiddenCount: (res.data && res.data.hidden_count) || 0
        }
        const available = new Set(this.overlays.map((item) => item.filename))
        this.selectedFiles = this.selectedFiles.filter((name) => available.has(name))
      } catch (error) {
        this.$message.error(error.response?.data?.detail || (this.isEn ? 'Failed to load images' : '加载图片失败'))
      } finally {
        this.loading = false
      }
    },
    imageUrl(filename) {
      const params = new URLSearchParams()
      params.append('t', Date.now())
      const token = localStorage.getItem('access_token')
      if (token) params.append('token', token)
      return `${BASE_URL}/admin/manual-archive/combo-overlays/${encodeURIComponent(filename)}?${params.toString()}`
    },
    isSelected(filename) {
      return this.selectedFiles.includes(filename)
    },
    toggleSelection(filename) {
      if (this.isSelected(filename)) {
        this.selectedFiles = this.selectedFiles.filter((name) => name !== filename)
      } else {
        this.selectedFiles = [...this.selectedFiles, filename]
      }
    },
    formatSize(size) {
      if (size == null) return '-'
      if (size < 1024) return `${size} B`
      if (size < 1024 * 1024) return `${(size / 1024).toFixed(1)} KB`
      return `${(size / 1024 / 1024).toFixed(1)} MB`
    },
    async confirmSelected() {
      if (!this.selectedFiles.length) return
      this.confirming = true
      try {
        const res = await this.$http.post(`${BASE_URL}/admin/manual-archive/combo-overlays/confirm`, {
          filenames: this.selectedFiles
        })
        const count = res.data?.written_count || 0
        const dataset = res.data?.dataset_name || ''
        this.preparedDataset = {
          selectedCount: res.data?.selected_count || this.selectedFiles.length,
          sampleCount: count,
          datasetName: dataset
        }
        this.trainingTaskId = ''
        this.trainingTaskStatus = ''
        this.saveModelUpdateState()
        this.trainingEpoch = null
        this.trainingMessage = ''
        this.stopTrainingPolling()
        this.currentStep = 'train'
        this.saveModelUpdateState()
        this.$message.success(
          this.isEn
            ? `Prepared ${count} image/label files${dataset ? ` in ${dataset}` : ''}.`
            : `已生成 ${count} 个 image/label 文件${dataset ? `，保存至 ${dataset}` : ''}。`
        )
        this.$nextTick(() => {
          this.$refs.trainSection?.scrollIntoView({ behavior: 'smooth', block: 'start' })
        })
      } catch (error) {
        this.$message.error(error.response?.data?.detail || (this.isEn ? 'Failed to confirm data' : '确认新增数据失败'))
      } finally {
        this.confirming = false
      }
    },
    async deleteSelected() {
      if (!this.selectedFiles.length) return
      try {
        await this.$confirm(
          this.isEn
            ? `Delete ${this.selectedFiles.length} selected image group(s)?`
            : `确定删除已选择的 ${this.selectedFiles.length} 组图片吗？`,
          this.isEn ? 'Notice' : '提示',
          { type: 'warning' }
        )
      } catch (error) {
        return
      }

      this.deleting = true
      try {
        const res = await this.$http.post(`${BASE_URL}/admin/manual-archive/hide-combo-overlays`, {
          filenames: this.selectedFiles
        })
        const count = res.data?.removed_count || 0
        this.$message.success(
          this.isEn
            ? `Deleted ${count} file(s).`
            : `已删除 ${count} 个文件。`
        )
        this.selectedFiles = []
        await this.loadOverlays()
      } catch (error) {
        this.$message.error(error.response?.data?.detail || (this.isEn ? 'Delete failed' : '删除失败'))
      } finally {
        this.deleting = false
      }
    },
    async startTraining() {
      if (!this.preparedDataset?.datasetName) return
      this.training = true
      this.trainingMessage = ''
      try {
        const res = await this.$http.post(`${BASE_URL}/admin/manual-archive/train`, {
          dataset_name: this.preparedDataset.datasetName
        })
        this.trainingTaskId = res.data?.task_id || ''
        this.trainingTaskStatus = 'processing'
        this.trainingEpoch = { current: 0, total: 500 }
        this.currentStep = 'train'
        this.saveModelUpdateState()
        this.startTrainingPolling()
        this.$message.success(
          this.isEn
            ? `Training started${this.trainingTaskId ? `: ${this.trainingTaskId}` : ''}.`
            : `训练已启动${this.trainingTaskId ? `：${this.trainingTaskId}` : ''}。`
        )
      } catch (error) {
        this.$message.error(error.response?.data?.detail || (this.isEn ? 'Failed to start training' : '启动训练失败'))
      } finally {
        this.training = false
        this.saveModelUpdateState()
      }
    },
    async stopTraining() {
      if (!this.trainingTaskId) return
      try {
        await this.$confirm(
          this.isEn ? 'Stop the current training task?' : '确定停止当前训练任务吗？',
          this.isEn ? 'Notice' : '提示',
          { type: 'warning' }
        )
      } catch (error) {
        return
      }

      this.stoppingTraining = true
      this.trainingTaskStatus = 'stopping'
      this.trainingMessage = this.isEn ? 'Stopping training...' : '正在停止训练...'
      this.saveModelUpdateState()
      try {
        const res = await this.$http.post(`${BASE_URL}/admin/manual-archive/train/${encodeURIComponent(this.trainingTaskId)}/stop`)
        const data = res.data || {}
        this.trainingTaskStatus = data.status || 'stopped'
        if (data.training_epoch) {
          this.trainingEpoch = data.training_epoch
        }
        this.trainingMessage = data.message || (this.isEn ? 'Training stopped.' : '训练已停止。')
        this.stopTrainingPolling()
        this.saveModelUpdateState()
        this.$message.success(this.isEn ? 'Training stopped.' : '训练已停止。')
      } catch (error) {
        this.trainingTaskStatus = 'processing'
        this.trainingMessage = ''
        this.saveModelUpdateState()
        this.startTrainingPolling()
        this.$message.error(error.response?.data?.detail || (this.isEn ? 'Failed to stop training' : '停止训练失败'))
      } finally {
        this.stoppingTraining = false
      }
    },
    startTrainingPolling() {
      this.stopTrainingPolling()
      if (!this.trainingTaskId) return
      this.fetchTrainingStatus()
      this.trainingPollTimer = setInterval(this.fetchTrainingStatus, 3000)
    },
    stopTrainingPolling() {
      if (this.trainingPollTimer) {
        clearInterval(this.trainingPollTimer)
        this.trainingPollTimer = null
      }
    },
    async fetchTrainingStatus() {
      if (!this.trainingTaskId) return
      try {
        const res = await this.$http.get(`${BASE_URL}/task_status/${this.trainingTaskId}`)
        const data = res.data || {}
        this.trainingTaskStatus = data.status || ''
        this.trainingMessage = data.message || ''
        if (data.training_epoch) {
          this.trainingEpoch = data.training_epoch
        }
        this.saveModelUpdateState()
        if (data.status === 'completed') {
          this.stopTrainingPolling()
          this.saveModelUpdateState()
          this.$message.success(this.isEn ? 'Training completed.' : '训练完成。')
        } else if (data.status === 'failed') {
          this.stopTrainingPolling()
          this.saveModelUpdateState()
          this.$message.error(data.message || (this.isEn ? 'Training failed.' : '训练失败。'))
        } else if (data.status === 'stopped') {
          this.stopTrainingPolling()
          this.saveModelUpdateState()
        }
      } catch (error) {
        this.stopTrainingPolling()
        this.trainingTaskStatus = ''
        this.saveModelUpdateState()
        this.$message.error(error.response?.data?.detail || (this.isEn ? 'Failed to get training status' : '获取训练状态失败'))
      }
    }
  }
}
</script>

<style scoped>
.model-page {
  min-height: 100vh;
  padding: 28px;
  background: linear-gradient(180deg, #eef9ff 0%, #f7fbff 100%);
  color: #111827;
}
.review-section {
  background: #fff;
  border-radius: 10px;
  padding: 18px 20px 20px;
  box-shadow: 0 8px 24px rgba(15, 23, 42, 0.08);
}
.model-nav {
  display: flex;
  justify-content: flex-end;
  margin-bottom: 16px;
}
.review-actions {
  display: flex;
  align-items: center;
  gap: 14px;
}
.train-section {
  min-height: 220px;
  margin-top: 20px;
  background: #fff;
  border-radius: 10px;
  padding: 18px 20px;
  box-shadow: 0 8px 24px rgba(15, 23, 42, 0.08);
}
.train-section h2 {
  margin: 0;
  font-size: 22px;
}
.history-section {
  min-height: 220px;
  background: #fff;
  border-radius: 10px;
  padding: 18px 20px;
  box-shadow: 0 8px 24px rgba(15, 23, 42, 0.08);
}
.history-section h2 {
  margin: 0;
  font-size: 22px;
}
.history-table {
  margin-top: 16px;
}
.dataset-summary {
  margin: 14px 0 0;
  color: #475569;
  line-height: 1.6;
}
.training-actions {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-top: 16px;
}
.train-button {
  margin-top: 0;
}
.training-progress {
  margin: 12px 0 0;
  font-weight: 600;
  color: #0f766e;
}
.training-status {
  margin: 8px 0 0;
  font-weight: 600;
  color: #475569;
}
.training-task {
  margin: 12px 0 0;
  color: #64748b;
  font-size: 13px;
}
.section-head {
  display: flex;
  justify-content: space-between;
  gap: 16px;
  align-items: flex-start;
  margin-bottom: 18px;
}
.section-head h2 {
  margin: 0 0 6px;
  font-size: 22px;
}
.section-head p {
  margin: 0;
  color: #64748b;
  line-height: 1.5;
}
.overlay-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(240px, 1fr));
  gap: 14px;
  min-height: 160px;
}
.overlay-item {
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  padding: 10px;
  background: #f8fafc;
  transition: border-color 0.2s, box-shadow 0.2s;
}
.overlay-item.selected {
  border-color: #0a84ff;
  box-shadow: 0 0 0 2px rgba(10, 132, 255, 0.12);
}
.item-top {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 8px;
}
.filename {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: 12px;
  color: #334155;
}
.overlay-image {
  width: 100%;
  height: 180px;
  background: #000;
  border-radius: 6px;
  overflow: hidden;
}
.meta-row {
  display: flex;
  justify-content: space-between;
  gap: 8px;
  margin-top: 8px;
  font-size: 12px;
  color: #64748b;
}
.confirm-bar {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 14px;
  margin-top: 18px;
}
@media (max-width: 720px) {
  .model-page {
    padding: 14px;
  }
  .section-head,
  .confirm-bar {
    flex-direction: column;
    align-items: stretch;
  }
}
</style>
